import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'
import { AnimatedHeading } from '@/components/motion/animated-heading'
import { Reveal } from '@/components/motion/reveal'
import { siteConfig } from '@/constants/site'

interface CtaBandProps {
    eyebrow?: string
    title: string
    body?: string
    primary: { label: string, href: string }
    secondary?: { label: string, href: string } | 'call' | null
    className?: string
}

export const CtaBand: FC<CtaBandProps> = ({
    eyebrow = "Ready when you are",
    title,
    body,
    primary,
    secondary = 'call',
    className
}): ReactNode => {
    return (
        <section className={cn('py-16 md:py-24', className)}>
            <Container>
                <div className='relative overflow-hidden rounded-[32px] bg-ink px-6 py-14 text-cream sm:px-10 md:rounded-[40px] md:px-14 md:py-20 lg:px-20'>
                    <div className='door-grooves pointer-events-none absolute inset-0 opacity-60' aria-hidden='true' />
                    <div className='bg-noise pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay' aria-hidden='true' />

                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute -bottom-48 -right-24 size-[34rem] rounded-full bg-ember/30 blur-[120px]'
                    />

                    <div className='relative grid gap-10 lg:grid-cols-12 lg:items-end'>
                        <div className='lg:col-span-8'>
                            <Eyebrow tone='light'>{eyebrow}</Eyebrow>

                            <AnimatedHeading className='mt-5 max-w-[20ch] text-h2 font-semibold text-balance text-cream'>
                                {title}
                            </AnimatedHeading>

                            {body && (
                                <Reveal className='mt-6 max-w-xl text-lead text-cream/65'>
                                    <p>{body}</p>
                                </Reveal>
                            )}
                        </div>

                        <Reveal selector=':scope > *' className='flex flex-col gap-3 sm:flex-row lg:col-span-4 lg:flex-col lg:items-end'>
                            <ButtonLink href={primary.href} size='lg'>
                                {primary.label}
                            </ButtonLink>

                            {secondary === 'call' && (
                                <ButtonLink href={siteConfig.phoneHref} variant='outline-light' size='lg' icon='phone'>
                                    Call {siteConfig.phone}
                                </ButtonLink>
                            )}

                            {secondary && secondary !== 'call' && (
                                <ButtonLink href={secondary.href} variant='outline-light' size='lg'>
                                    {secondary.label}
                                </ButtonLink>
                            )}
                        </Reveal>
                    </div>
                </div>
            </Container>
        </section>
    )
}
